import { useMemo } from "react";
import { GENDER_TYPE } from "../../../configs/constants/GenderType";
import { CompetenceItem } from "../../../models/Competence";
import { ConsultantsListResponse } from "../../../models/Consultant";
import { cityKey } from "../../../models/ConsultantFilter";
import { useAllConsultants } from "../../../networking/consultant.hooks";
import {
	useCompetence,
	useConsultantManagers,
	useOrganisation,
} from "../../../networking/organisation.hooks";
import { useSubsidiaries } from "../../../networking/subsidiary.hooks";
import { FilterOptions } from "./FilterBuilder";

/**
 * Bundles everything the filter UI needs: the org competence tree, the manager
 * id→name map, and the distinct filterable values (genders / managers / org
 * units) present in the loaded consultant list. Shared by the consultant-list
 * filter drawer and the org-defined-filters settings editor so both offer the
 * exact same options.
 */
export function useFilterData(pool: string = "") {
	const { consultants, isLoading } = useAllConsultants(pool);

	const { data: competenceData } = useCompetence();
	const competenceTree = (competenceData?.competences ?? []) as CompetenceItem[];

	// Certification/tag options come straight from the org taxonomies (like the
	// competence tree) so filters can be built before any consultant carries them.
	const { data: organisation } = useOrganisation();

	// Straight from the org's subsidiaries (like the taxonomies) so the filter
	// can be built before any consultant is connected to one.
	const { subsidiaries } = useSubsidiaries();

	// The consultant's profile.manager only carries an id, so resolve names from
	// the org's consultant managers (id → "First Last" + whether the account is
	// still active — the filter tucks deactivated managers behind a link).
	const { consultantManagers } = useConsultantManagers();
	const managerById = useMemo(() => {
		const map = new Map<string, { name: string; active: boolean }>();
		consultantManagers.forEach((m) =>
			map.set(m.id, {
				name: [m.firstName, m.lastName].filter(Boolean).join(" ") || m.id,
				active: m.active !== false,
			})
		);
		return map;
	}, [consultantManagers]);

	const managerNameById = useMemo(() => {
		const map = new Map<string, string>();
		managerById.forEach((m, id) => map.set(id, m.name));
		return map;
	}, [managerById]);

	const options = useMemo<FilterOptions>(() => {
		const genders = new Set<GENDER_TYPE>();
		const managerIds = new Set<string>();
		// Home towns are free text: group by canonical key and show the most
		// common spelling as the label, so "Uppsala" and "UPPSALA" become one
		// option that matches both.
		const citySpellings = new Map<string, Map<string, number>>();

		for (const c of consultants as ConsultantsListResponse[]) {
			if (c.profile?.gender) genders.add(c.profile.gender);
			if (c.profile?.manager?.id) managerIds.add(c.profile.manager.id);

			const key = cityKey(c.profile?.address?.city);
			if (key) {
				const spelling = (c.profile?.address?.city ?? "").trim().replace(/\s+/g, " ");
				const counts = citySpellings.get(key) ?? new Map<string, number>();
				counts.set(spelling, (counts.get(spelling) ?? 0) + 1);
				citySpellings.set(key, counts);
			}
		}

		const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, "sv");

		return {
			genders: [...genders],
			// Managers no longer in the org's account list (removed users) count as
			// inactive so they don't clutter the default list either.
			managers: [...managerIds]
				.map((id) => ({
					id,
					name: managerById.get(id)?.name || id,
					active: managerById.get(id)?.active ?? false,
				}))
				.sort(byName),
			cities: [...citySpellings.entries()]
				.map(([key, counts]) => ({
					key,
					name: [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0],
				}))
				.sort(byName),
			certificationTypes: (organisation?.certificationTypes ?? [])
				.map((c) => ({ key: c.key, name: c.name?.sv || c.key }))
				.sort(byName),
			consultantTagTypes: (organisation?.consultantTagTypes ?? [])
				.map((tt) => ({ key: tt.key, name: tt.name?.sv || tt.key }))
				.sort(byName),
			subsidiaries: subsidiaries.map((s) => ({ id: s.id, name: s.name })).sort(byName),
		};
	}, [consultants, managerById, organisation, subsidiaries]);

	return { consultants, options, competenceTree, managerNameById, isLoading };
}
